// Command palette (Ctrl/⌘K): fields, commands, recent files.
import { S, undo, redo, select } from './state.js';
import { $, h, kbd } from './dom.js';
import { icon, KIND_LABEL } from './icons.js';
import * as A from './actions.js';
import * as V from './view.js';
import { openDialog, newBlank, save, listRecent, openRecent, exportForPrinter } from './io.js';
import { exportPNG, printProof } from './export.js';
import { cycleTheme } from './theme.js';
import { openCheckPanel } from './check.js';
import { displayText } from './geometry.js';

let focusIdx = 0;
let items = [];
let lastFocus = null;
let seq = 0;

export const isCmdKOpen = () => $('#cmdk-overlay').classList.contains('visible');

export function openCmdK(prefill = '') {
  lastFocus = document.activeElement;
  $('#cmdk-overlay').classList.add('visible');
  const input = $('#cmdk-input');
  input.value = prefill;
  focusIdx = 0;
  render();
  setTimeout(() => input.focus(), 20);
}
export function closeCmdK() {
  if (!isCmdKOpen()) return;
  $('#cmdk-overlay').classList.remove('visible');
  if (lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus();
}

function commands() {
  const has = !!S.label;
  const sel = S.selection.length > 0;
  const c = (group, ic, label, run, opts = {}) => ({ group, icon: ic, label, run, ...opts });
  return [
    c('File', 'open', 'Open .itf / .image file…', openDialog, { kbd: kbd('Mod+O') }),
    c('File', 'new', 'New blank label', newBlank, { kbd: kbd('Mod+Alt+N') }),
    has && c('File', 'save', 'Save', () => save('save'), { kbd: kbd('Mod+S') }),
    has && c('File', 'save', 'Save as…', () => save('as'), { kbd: kbd('Mod+Shift+S') }),
    has && c('File', 'download', 'Download a copy', () => save('download')),
    has && c('File', 'print', 'Export for printer (.image + .job)', exportForPrinter),
    has && c('File', 'png', 'Export PNG proof (305 dpi)', exportPNG),
    has && c('File', 'print', 'Print proof (PDF via browser print)', printProof),
    has && c('Add', 'add', 'Add text field', () => A.addFieldOfKind('text'), { kbd: 'T' }),
    has && c('Add', 'add', 'Add date field', () => A.addFieldOfKind('date'), { kbd: 'D' }),
    has && c('Add', 'add', 'Add barcode field', () => A.addFieldOfKind('barcode'), { kbd: 'B' }),
    has && c('Edit', 'undo', 'Undo', undo, { kbd: kbd('Mod+Z') }),
    has && c('Edit', 'redo', 'Redo', redo, { kbd: kbd('Mod+Shift+Z') }),
    sel && c('Edit', 'dup', 'Duplicate selected', A.duplicateSelected, { kbd: kbd('Mod+D') }),
    sel && c('Edit', 'rotate', 'Rotate selected 90°', () => A.rotateSelected(90), { kbd: 'R' }),
    sel && c('Edit', 'lock', 'Lock / unlock (editor only)', A.toggleLockSelected, { kbd: kbd('Mod+L') }),
    sel && c('Edit', 'front', 'Bring to front', A.bringToFront, { kbd: kbd('Mod+Shift+]') }),
    sel && c('Edit', 'back', 'Send to back', A.sendToBack, { kbd: kbd('Mod+Shift+[') }),
    sel && c('Edit', 'del', 'Delete selected', A.deleteSelected, { kbd: 'Del' }),
    has && c('Edit', 'field', 'Select all fields', A.selectAll, { kbd: kbd('Mod+A') }),
    has && c('View', 'check', 'Printer check', openCheckPanel),
    has && c('View', 'fit', 'Fit to view', V.fitToView, { kbd: 'F' }),
    has && c('View', 'fit', 'Actual size (1:1)', V.actualSize, { kbd: '1' }),
    c('View', 'ruler', 'Calibrate screen size…', V.openDPICalibration),
    c('View', 'grid', 'Toggle grid', V.toggleGrid, { kbd: 'G' }),
    c('View', 'ruler', 'Toggle rulers', V.toggleRulers),
    c('View', 'theme', 'Switch theme (System → Light → Dark)', cycleTheme),
    c('Help', 'help', 'Keyboard shortcuts', () => import('./shortcuts.js').then((m) => m.openShortcuts()), { kbd: '?' }),
  ].filter(Boolean);
}

async function buildItems(q) {
  const fieldItems = S.fields.map((f) => ({
    group: 'Fields',
    icon: 'field',
    label: f.name,
    sub: `${KIND_LABEL[f.kind] || f.kind} · ${(f.x / 100).toFixed(1)}, ${(f.y / 100).toFixed(1)} mm${f.kind === 'text' ? ` · ${String(f.text).slice(0, 40)}` : f.kind === 'date' ? ` · ${displayText(f)}` : ''}`,
    run: () => {
      select([f.key]);
      V.scrollToField(f.key);
    },
  }));
  let recent = [];
  if (!q || 'recent'.startsWith(q) || q.startsWith('recent')) {
    recent = (await listRecent()).map((r) => ({ group: 'Recent', icon: 'recent', label: r.name, sub: new Date(r.ts).toLocaleString(), run: () => openRecent(r.name) }));
  }
  const all = [...fieldItems, ...commands(), ...recent];
  const query = q.replace(/^recent\s*/, '');
  if (!query) return q.startsWith('recent') ? recent : all;
  return all.filter((it) => `${it.label} ${it.sub || ''} ${it.group}`.toLowerCase().includes(query));
}

async function render() {
  const my = ++seq;
  const q = $('#cmdk-input').value.trim().toLowerCase();
  const next = await buildItems(q);
  if (my !== seq) return;
  items = next;
  const list = $('#cmdk-list');
  const input = $('#cmdk-input');
  if (!items.length) {
    list.replaceChildren(h('div', { class: 'cmdk-empty' }, 'No results'));
    input.removeAttribute('aria-activedescendant');
    return;
  }
  focusIdx = Math.max(0, Math.min(focusIdx, items.length - 1));
  const nodes = [];
  let group = null;
  items.forEach((it, i) => {
    if (it.group !== group) {
      group = it.group;
      nodes.push(h('div', { class: 'cmdk-group-label', role: 'presentation' }, group));
    }
    nodes.push(
      h(
        'div',
        {
          class: `cmdk-item${i === focusIdx ? ' focused' : ''}`,
          id: `cmdk-item-${i}`,
          role: 'option',
          'aria-selected': String(i === focusIdx),
          onClick: () => runItem(it),
          onMousemove: () => {
            if (focusIdx !== i) {
              focusIdx = i;
              markFocus();
            }
          },
        },
        icon(it.icon),
        h('div', null, h('div', { class: 'cmdk-item-label' }, it.label), it.sub ? h('div', { class: 'cmdk-item-sub' }, it.sub) : null),
        it.kbd ? h('span', { class: 'kbd', style: { fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--ink-3)' } }, it.kbd) : h('span'),
      ),
    );
  });
  list.replaceChildren(...nodes);
  markFocus();
}

function markFocus() {
  const list = $('#cmdk-list');
  for (const el of list.querySelectorAll('.cmdk-item')) {
    const on = el.id === `cmdk-item-${focusIdx}`;
    el.classList.toggle('focused', on);
    el.setAttribute('aria-selected', String(on));
    if (on) el.scrollIntoView({ block: 'nearest' });
  }
  $('#cmdk-input').setAttribute('aria-activedescendant', `cmdk-item-${focusIdx}`);
}

function runItem(it) {
  closeCmdK();
  try {
    it.run();
  } catch (err) {
    console.error(err);
  }
}

export function initCmdK() {
  const input = $('#cmdk-input');
  input.addEventListener('input', () => {
    focusIdx = 0;
    render();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusIdx = Math.min(items.length - 1, focusIdx + 1);
      markFocus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusIdx = Math.max(0, focusIdx - 1);
      markFocus();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (items[focusIdx]) runItem(items[focusIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeCmdK();
    } else if (e.key === 'Tab') {
      e.preventDefault();
    }
  });
  $('#cmdk-overlay').addEventListener('mousedown', (e) => {
    if (e.target.id === 'cmdk-overlay') closeCmdK();
  });
  $('#btn-cmdk').onclick = () => openCmdK();
}
